import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Trash2,
  Upload,
  Play,
  Loader2,
  CheckCircle,
  AlertCircle,
  X,
  Image as ImageIcon,
  FlaskConical,
} from 'lucide-react';
import { supabase, STORAGE_BUCKET } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { ROOMS } from '@/lib/roomConfig';
import { GenerationType, AspectRatio, StagingStyle } from '@/types';
import { generateRoomImage } from '@/services/geminiService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// ============================================================================
// Types
// ============================================================================

type TestGenType = 'Empty' | 'Staged' | 'Both';
type ImageResultStatus = 'idle' | 'running-empty' | 'running-staged' | 'done' | 'error';

interface TestImageConfig {
  roomType: string;
  generationType: TestGenType;
  aspectRatio: AspectRatio;
  stagingStyle: StagingStyle;
  keepKitchen: boolean;
}

interface PersistedResult {
  emptyUrl?: string;
  emptyStoragePath?: string;
  stagedUrl?: string;
  stagedStoragePath?: string;
}

interface TestImage {
  id: string;
  fileName: string;
  storagePath: string;
  publicUrl: string;
  config: TestImageConfig;
  lastResult?: PersistedResult;
}

interface TestSet {
  id: string;
  name: string;
  images: TestImage[];
  createdAt: number;
}

interface ImageResult {
  status: ImageResultStatus;
  emptyUrl?: string;
  stagedUrl?: string;
  error?: string;
}

// ============================================================================
// Constants
// ============================================================================

const LS_SETS_KEY = 'admin_prompt_test_sets';
const CONCURRENCY_LIMIT = 2;

const DEFAULT_CONFIG: TestImageConfig = {
  roomType: 'living_room',
  generationType: 'Both',
  aspectRatio: '4:3',
  stagingStyle: 'modern',
  keepKitchen: false,
};

// ============================================================================
// Concurrent runner
// ============================================================================

async function runConcurrent(tasks: (() => Promise<void>)[], limit: number): Promise<void> {
  const active = new Set<Promise<void>>();
  for (const task of tasks) {
    // eslint-disable-next-line no-async-promise-executor
    const p: Promise<void> = new Promise(async (resolve) => {
      try { await task(); } catch { /* errors handled inside task */ }
      resolve();
    });
    p.then(() => active.delete(p));
    active.add(p);
    if (active.size >= limit) {
      await Promise.race(active);
    }
  }
  await Promise.all(active);
}

// ============================================================================
// TestImageCard
// ============================================================================

interface TestImageCardProps {
  image: TestImage;
  result: ImageResult;
  onDelete: () => void;
  onConfigChange: (field: keyof TestImageConfig, value: string | boolean) => void;
}

const TestImageCard: React.FC<TestImageCardProps> = ({ image, result, onDelete, onConfigChange }) => {
  const { config } = image;
  const roomConfig = ROOMS[config.roomType];
  const supportsStaging = roomConfig?.supportsStaging ?? true;
  const isKitchen = config.roomType === 'kitchen';
  const includesEmpty = config.generationType === 'Empty' || config.generationType === 'Both';
  const includesStaged = config.generationType === 'Staged' || config.generationType === 'Both';

  const StatusBadge: React.FC = () => {
    switch (result.status) {
      case 'running-empty':
        return <span className="text-xs text-amber-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Empty...</span>;
      case 'running-staged':
        return <span className="text-xs text-amber-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Staged...</span>;
      case 'done':
        return <span className="text-xs text-emerald-500 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Fertig</span>;
      case 'error':
        return <span className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" />Fehler</span>;
      default:
        return <span className="text-xs text-muted-foreground">Idle</span>;
    }
  };

  return (
    <div className="border border-border rounded-lg p-3 flex gap-3 bg-card">
      {/* Source image */}
      <div className="flex-shrink-0">
        <a href={image.publicUrl} target="_blank" rel="noopener noreferrer">
          <img
            src={image.publicUrl}
            alt={image.fileName}
            className="w-32 h-24 object-cover rounded hover:opacity-80 transition-opacity"
          />
        </a>
        <p className="text-[10px] text-muted-foreground mt-1 truncate w-32">{image.fileName}</p>
        <p className="text-[10px] text-muted-foreground/60 truncate w-32">Original</p>
      </div>

      {/* Config controls */}
      <div className="flex flex-col gap-1.5 text-xs flex-shrink-0 w-48">
        <div>
          <label className="text-muted-foreground block mb-0.5">Raum</label>
          <select
            value={config.roomType}
            onChange={e => onConfigChange('roomType', e.target.value)}
            className="w-full rounded border border-border bg-background px-1.5 py-1 text-xs"
          >
            <optgroup label="Innenbereich">
              {Object.values(ROOMS).filter(r => r.category === 'indoor').map(r => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </optgroup>
            <optgroup label="Außenbereich">
              {Object.values(ROOMS).filter(r => r.category === 'outdoor').map(r => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </optgroup>
          </select>
        </div>

        <div>
          <label className="text-muted-foreground block mb-0.5">Generierungstyp</label>
          <select
            value={!supportsStaging && config.generationType !== 'Empty' ? 'Empty' : config.generationType}
            onChange={e => onConfigChange('generationType', e.target.value)}
            className="w-full rounded border border-border bg-background px-1.5 py-1 text-xs"
          >
            <option value="Empty">Empty</option>
            {supportsStaging && <option value="Staged">Staged</option>}
            {supportsStaging && <option value="Both">Both</option>}
          </select>
        </div>

        <div>
          <label className="text-muted-foreground block mb-0.5">Seitenverhältnis</label>
          <select
            value={config.aspectRatio}
            onChange={e => onConfigChange('aspectRatio', e.target.value)}
            className="w-full rounded border border-border bg-background px-1.5 py-1 text-xs"
          >
            <option value="4:3">4:3 Querformat</option>
            <option value="1:1">1:1 Quadrat</option>
          </select>
        </div>

        {includesStaged && supportsStaging && (
          <div>
            <label className="text-muted-foreground block mb-0.5">Stil</label>
            <select
              value={config.stagingStyle}
              onChange={e => onConfigChange('stagingStyle', e.target.value)}
              className="w-full rounded border border-border bg-background px-1.5 py-1 text-xs"
            >
              <option value="modern">Modern</option>
              <option value="luxury">Luxuriös</option>
              <option value="industrial">Industrial</option>
            </select>
          </div>
        )}

        {isKitchen && includesEmpty && (
          <label className="flex items-center gap-1.5 cursor-pointer mt-0.5">
            <input
              type="checkbox"
              checked={config.keepKitchen}
              onChange={e => onConfigChange('keepKitchen', e.target.checked)}
              className="rounded"
            />
            <span className="text-muted-foreground">Küche behalten</span>
          </label>
        )}
      </div>

      {/* Status + results */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-2">
          <StatusBadge />
          <button
            onClick={onDelete}
            className="text-muted-foreground hover:text-destructive transition-colors"
            title="Bild löschen"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>

        {result.error && (
          <p className="text-xs text-destructive mb-2 bg-destructive/10 rounded px-2 py-1 break-words">
            {result.error}
          </p>
        )}

        <div className="flex gap-2 flex-wrap">
          {result.emptyUrl && (
            <div>
              <p className="text-[10px] text-muted-foreground mb-0.5">Empty</p>
              <a href={result.emptyUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={result.emptyUrl}
                  alt="Empty result"
                  className="w-32 h-24 object-cover rounded hover:opacity-80 transition-opacity"
                />
              </a>
            </div>
          )}
          {result.stagedUrl && (
            <div>
              <p className="text-[10px] text-muted-foreground mb-0.5">Staged</p>
              <a href={result.stagedUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={result.stagedUrl}
                  alt="Staged result"
                  className="w-32 h-24 object-cover rounded hover:opacity-80 transition-opacity"
                />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// AdminPromptTestView
// ============================================================================

export const AdminPromptTestView: React.FC = () => {
  const { user } = useAuth();

  const [testSets, setTestSets] = useState<TestSet[]>([]);
  const [activeSetId, setActiveSetId] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, ImageResult>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [newSetName, setNewSetName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);

  // ============================================================================
  // Persistence
  // ============================================================================

  const saveTestSets = useCallback((sets: TestSet[]) => {
    localStorage.setItem(LS_SETS_KEY, JSON.stringify(sets));
    setTestSets(sets);
  }, []);

  // ============================================================================
  // Init
  // ============================================================================

  useEffect(() => {
    const raw = localStorage.getItem(LS_SETS_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as TestSet[];
        setTestSets(parsed);
        if (parsed.length > 0) setActiveSetId(parsed[0].id);

        // Hydrate results state from persisted lastResult
        const hydrated: Record<string, ImageResult> = {};
        for (const set of parsed) {
          for (const img of set.images) {
            if (img.lastResult) {
              hydrated[img.id] = {
                status: 'done',
                emptyUrl: img.lastResult.emptyUrl,
                stagedUrl: img.lastResult.stagedUrl,
              };
            }
          }
        }
        if (Object.keys(hydrated).length > 0) setResults(hydrated);
      } catch {
        // ignore corrupt localStorage data
      }
    }
  }, []);

  // ============================================================================
  // Test set management
  // ============================================================================

  const handleCreateSet = () => {
    const name = newSetName.trim() || `Test Set ${testSets.length + 1}`;
    const newSet: TestSet = {
      id: crypto.randomUUID(),
      name,
      images: [],
      createdAt: Date.now(),
    };
    const updated = [newSet, ...testSets];
    saveTestSets(updated);
    setActiveSetId(newSet.id);
    setNewSetName('');
  };

  const handleDeleteSet = async (setId: string) => {
    const set = testSets.find(s => s.id === setId);
    if (!set) return;

    const paths = set.images.flatMap(img => [
      img.storagePath,
      img.lastResult?.emptyStoragePath,
      img.lastResult?.stagedStoragePath,
    ]).filter(Boolean) as string[];
    if (paths.length > 0) {
      await supabase.storage.from(STORAGE_BUCKET).remove(paths);
    }

    const updated = testSets.filter(s => s.id !== setId);
    saveTestSets(updated);
    if (activeSetId === setId) {
      setActiveSetId(updated.length > 0 ? updated[0].id : null);
    }
  };

  // ============================================================================
  // Image management
  // ============================================================================

  const handleUpload = async (files: FileList | null) => {
    if (!files || !activeSetId || !user) return;
    setIsUploading(true);
    setUploadErrors([]);

    const newImages: TestImage[] = [];
    const errors: string[] = [];

    for (const file of Array.from(files)) {
      const ext = file.name.split('.').pop() || 'jpg';
      const id = crypto.randomUUID();
      const storagePath = `admin-test/${user.id}/sources/${id}.${ext}`;

      const { error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(storagePath, file, { cacheControl: '3600', upsert: false });

      if (error) {
        errors.push(`${file.name}: ${error.message}`);
        continue;
      }

      const { data: urlData } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(storagePath);
      newImages.push({
        id,
        fileName: file.name,
        storagePath,
        publicUrl: urlData.publicUrl,
        config: { ...DEFAULT_CONFIG },
      });
    }

    if (errors.length > 0) setUploadErrors(errors);

    const updated = testSets.map(s =>
      s.id === activeSetId
        ? { ...s, images: [...s.images, ...newImages] }
        : s
    );
    saveTestSets(updated);
    setIsUploading(false);
  };

  const handleDeleteImage = async (imageId: string) => {
    const set = testSets.find(s => s.id === activeSetId);
    const img = set?.images.find(i => i.id === imageId);
    if (!img) return;

    const pathsToDelete = [
      img.storagePath,
      img.lastResult?.emptyStoragePath,
      img.lastResult?.stagedStoragePath,
    ].filter(Boolean) as string[];
    await supabase.storage.from(STORAGE_BUCKET).remove(pathsToDelete);

    const updated = testSets.map(s =>
      s.id === activeSetId
        ? { ...s, images: s.images.filter(i => i.id !== imageId) }
        : s
    );
    saveTestSets(updated);

    setResults(prev => {
      const next = { ...prev };
      delete next[imageId];
      return next;
    });
  };

  const handleConfigChange = (imageId: string, field: keyof TestImageConfig, value: string | boolean) => {
    const updated = testSets.map(s =>
      s.id === activeSetId
        ? {
            ...s,
            images: s.images.map(img =>
              img.id === imageId
                ? { ...img, config: { ...img.config, [field]: value } }
                : img
            ),
          }
        : s
    );
    saveTestSets(updated);
  };

  // ============================================================================
  // Generation
  // ============================================================================

  const setImageResult = useCallback((imageId: string, update: Partial<ImageResult>) => {
    setResults(prev => ({
      ...prev,
      [imageId]: { ...(prev[imageId] ?? { status: 'idle' }), ...update },
    }));
  }, []);

  const persistResult = useCallback((imageId: string, update: Partial<PersistedResult>) => {
    setTestSets(prev => {
      const updated = prev.map(s => ({
        ...s,
        images: s.images.map(img =>
          img.id === imageId
            ? { ...img, lastResult: { ...(img.lastResult ?? {}), ...update } }
            : img
        ),
      }));
      localStorage.setItem(LS_SETS_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const runImage = useCallback(async (img: TestImage) => {
    if (!user) return;
    const { roomType, generationType, aspectRatio, stagingStyle, keepKitchen } = img.config;

    // Delete previously generated storage files before re-running
    if (img.lastResult) {
      const oldPaths = [img.lastResult.emptyStoragePath, img.lastResult.stagedStoragePath].filter(Boolean) as string[];
      if (oldPaths.length > 0) {
        await supabase.storage.from(STORAGE_BUCKET).remove(oldPaths);
      }
      persistResult(img.id, { emptyUrl: undefined, emptyStoragePath: undefined, stagedUrl: undefined, stagedStoragePath: undefined });
    }

    try {
      if (generationType === 'Empty' || generationType === 'Both') {
        setImageResult(img.id, { status: 'running-empty', error: undefined });
        const res = await generateRoomImage(
          img.storagePath,
          undefined,
          roomType,
          GenerationType.EMPTY,
          keepKitchen,
          undefined,
          undefined,
          undefined,
          undefined,
          user.id,
          aspectRatio,
          stagingStyle,
          true // adminTest
        );
        setImageResult(img.id, { emptyUrl: res.imageUrl });
        persistResult(img.id, { emptyUrl: res.imageUrl, emptyStoragePath: res.storagePath });
      }

      if (generationType === 'Staged' || generationType === 'Both') {
        setImageResult(img.id, { status: 'running-staged' });
        const res = await generateRoomImage(
          img.storagePath,
          undefined,
          roomType,
          GenerationType.STAGED,
          keepKitchen,
          undefined,
          undefined,
          undefined,
          undefined,
          user.id,
          aspectRatio,
          stagingStyle,
          true // adminTest
        );
        setImageResult(img.id, { stagedUrl: res.imageUrl });
        persistResult(img.id, { stagedUrl: res.imageUrl, stagedStoragePath: res.storagePath });
      }

      setImageResult(img.id, { status: 'done' });
    } catch (err) {
      setImageResult(img.id, {
        status: 'error',
        error: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    }
  }, [user, setImageResult, persistResult]);

  const handleRunAll = async () => {
    const set = testSets.find(s => s.id === activeSetId);
    if (!set || isRunning) return;

    setIsRunning(true);

    // Reset all results
    setResults(prev => {
      const reset = { ...prev };
      for (const img of set.images) {
        reset[img.id] = { status: 'idle' };
      }
      return reset;
    });

    const tasks = set.images.map(img => () => runImage(img));
    await runConcurrent(tasks, CONCURRENCY_LIMIT);

    setIsRunning(false);
  };

  // ============================================================================
  // Derived state
  // ============================================================================

  const activeSet = testSets.find(s => s.id === activeSetId) ?? null;
  const doneCount = activeSet?.images.filter(img =>
    results[img.id]?.status === 'done' || results[img.id]?.status === 'error'
  ).length ?? 0;
  const totalCount = activeSet?.images.length ?? 0;

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="flex gap-4" style={{ minHeight: '500px' }}>
      {/* Left: Test Set List */}
      <div className="w-52 flex-shrink-0 border border-border rounded-lg p-3 flex flex-col gap-2">
        <div className="flex items-center gap-1.5 mb-1">
          <FlaskConical className="w-3.5 h-3.5 text-muted-foreground" />
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Test Sets</p>
        </div>

        <div className="flex gap-1">
          <Input
            value={newSetName}
            onChange={e => setNewSetName(e.target.value)}
            placeholder="Name..."
            className="h-7 text-xs"
            onKeyDown={e => e.key === 'Enter' && handleCreateSet()}
          />
          <Button
            size="icon"
            variant="outline"
            className="h-7 w-7 flex-shrink-0"
            onClick={handleCreateSet}
          >
            <Plus className="w-3 h-3" />
          </Button>
        </div>

        <div className="flex flex-col gap-0.5 overflow-y-auto flex-1">
          {testSets.map(set => (
            <div
              key={set.id}
              className={`flex items-center justify-between rounded px-2 py-1.5 cursor-pointer text-xs group transition-colors ${
                activeSetId === set.id
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-foreground'
              }`}
              onClick={() => setActiveSetId(set.id)}
            >
              <span className="truncate flex-1">{set.name}</span>
              <span className={`text-[10px] mx-1 flex-shrink-0 ${activeSetId === set.id ? 'opacity-70' : 'text-muted-foreground'}`}>
                {set.images.length}
              </span>
              <button
                onClick={e => { e.stopPropagation(); handleDeleteSet(set.id); }}
                className={`opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ${
                  activeSetId === set.id ? 'hover:text-red-200' : 'hover:text-destructive'
                }`}
                title="Test Set löschen"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {testSets.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-6">Noch kein Test Set</p>
          )}
        </div>
      </div>

      {/* Right: Main Content */}
      <div className="flex-1 min-w-0 flex flex-col gap-3">
        {!activeSet ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm border-2 border-dashed border-border rounded-lg">
            Kein Test Set ausgewählt
          </div>
        ) : (
          <>
            {/* Top bar */}
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-semibold text-foreground">{activeSet.name}</h3>
              <span className="text-xs text-muted-foreground">
                {totalCount} {totalCount === 1 ? 'Bild' : 'Bilder'}
                {isRunning && ` · ${doneCount}/${totalCount} fertig`}
              </span>

              <div className="ml-auto flex gap-2">
                <label className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border cursor-pointer transition-colors ${
                  isUploading ? 'opacity-50 cursor-not-allowed bg-muted' : 'hover:bg-muted bg-background'
                }`}>
                  {isUploading
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <Upload className="w-3 h-3" />
                  }
                  Bilder hochladen
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    disabled={isUploading}
                    onChange={e => handleUpload(e.target.files)}
                  />
                </label>

                <Button
                  size="sm"
                  onClick={handleRunAll}
                  disabled={isRunning || activeSet.images.length === 0}
                >
                  {isRunning ? (
                    <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Läuft ({doneCount}/{totalCount})</>
                  ) : (
                    <><Play className="w-3 h-3 mr-1.5" />Run All</>
                  )}
                </Button>
              </div>
            </div>

            {/* Upload errors */}
            {uploadErrors.length > 0 && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-2">
                <p className="text-xs font-medium text-destructive mb-1">Upload-Fehler:</p>
                {uploadErrors.map((err, i) => (
                  <p key={i} className="text-xs text-destructive">{err}</p>
                ))}
              </div>
            )}

            {/* Image list */}
            {activeSet.images.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg text-muted-foreground gap-2 py-16">
                <ImageIcon className="w-8 h-8" />
                <p className="text-sm">Bilder hochladen um zu starten</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {activeSet.images.map(img => (
                  <TestImageCard
                    key={img.id}
                    image={img}
                    result={results[img.id] ?? { status: 'idle' }}
                    onDelete={() => handleDeleteImage(img.id)}
                    onConfigChange={(field, value) => handleConfigChange(img.id, field, value)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
